// InvoiceEmailDialog — диалог «Отправить письмо» на счёте (V15 «Почта» W7 + V17 W5a).
// Селект шаблона/аккаунта, Кому type=email, EmailBodyPreview (iframe sandbox / HTML),
// POST recipientEmail/subject/body если не blank; смена шаблона сбрасывает preview+override.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { emailApi } from '@/api/email';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';
import { EmailBodyPreview } from '@/pages/email/components/EmailBodyPreview';

/** Справочники шаблонов/аккаунтов в диалоге (нормально помещаются; размер ≤100). */
const OPTIONS_PAGE_SIZE = 100;

interface InvoiceEmailDialogProps {
  open: boolean;
  onClose: () => void;
  /** Счёт, по которому отправляется письмо (только OPEN — кнопка скрыта для остальных, §5 п.4). */
  invoice: Invoice;
}

/** Непустой Кому без `@` — заведомо битый адрес (дубль гварда BE). */
function isInvalidRecipient(email: string): boolean {
  return email.trim() !== '' && !email.includes('@');
}

/** Диалог «Отправить письмо»: шаблон + аккаунт + Кому + превью + отправка (202-контракт M-3). */
export function InvoiceEmailDialog({ open, onClose, invoice }: InvoiceEmailDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['email', 'templates'],
    queryFn: () => emailApi.listTemplates(0, OPTIONS_PAGE_SIZE),
    enabled: open,
    retry: false,
  });
  const settingsQuery = useQuery({
    queryKey: ['email', 'settings'],
    queryFn: () => emailApi.getSettings(),
    enabled: open,
    retry: false,
  });

  const templates = templatesQuery.data?.content ?? [];
  const accounts = settingsQuery.data?.accounts ?? [];
  // Дефолт аккаунта — активный (§5 п.5: activeAccountId); откат — первый в списке.
  const defaultAccountId = settingsQuery.data?.activeAccountId ?? accounts[0]?.id ?? null;
  // Дефолт шаблона — первый из списка (системный «Счёт» первым — is_system DESC, BE §3.5).
  const selectedTemplateId = templateId ?? templates[0]?.id ?? null;
  const selectedAccountId = accountId ?? defaultAccountId;
  const toInvalid = isInvalidRecipient(toEmail);

  const previewMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('Выберите шаблон');
      return emailApi.previewTemplate({ templateId: selectedTemplateId, invoiceId: invoice.id });
    },
    onSuccess: (data) => {
      setPreview({ subject: data.subject, body: data.body });
      setSubject(data.subject);
      setBody(data.body);
      setToEmail((current) => (current.trim() === '' ? (data.recipientEmail ?? '') : current));
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('Выберите шаблон');
      return invoicesApi.sendEmail(invoice.id, {
        templateId: selectedTemplateId,
        ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
        ...(toEmail.trim() ? { recipientEmail: toEmail.trim() } : {}),
        ...(subject ? { subject } : {}),
        ...(body ? { body } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email'] });
    },
  });

  const isPending = sendMutation.isPending || previewMutation.isPending;
  const sendError = sendMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(sendMutation.error, t('email.send.error'))}</Alert>
  ) : null;
  const previewError = previewMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(previewMutation.error, t('email.templates.previewError'))}</Alert>
  ) : null;
  const sendResult = sendMutation.data && sendMutation.data.status === 'FAILED' ? (
    <Alert severity="error">
      {t('email.send.failed', { error: sendMutation.data.error ?? t('email.send.error') })}
    </Alert>
  ) : sendMutation.data && sendMutation.data.status === 'SENT' ? (
    <Alert severity="success">{t('email.send.success')}</Alert>
  ) : null;

  const noAccounts = !settingsQuery.isLoading && accounts.length === 0;
  const noTemplates = !templatesQuery.isLoading && templates.length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('email.send.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {sendError}
          {sendResult}
          <TextField
            label={t('email.send.to')}
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            helperText={t('email.send.toHint')}
            inputProps={{ 'aria-label': t('email.send.to') }}
          />
          <TextField
            select
            label={t('email.send.template')}
            value={selectedTemplateId ?? ''}
            onChange={(e) => {
              setTemplateId(e.target.value || null);
              setPreview(null);
              setSubject('');
              setBody('');
            }}
            inputProps={{ 'aria-label': t('email.send.template') }}
          >
            {templates.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>

          {noAccounts ? (
            <Alert severity="warning">{t('email.send.notConfigured')}</Alert>
          ) : (
            <TextField
              select
              label={t('email.send.account')}
              value={selectedAccountId ?? ''}
              onChange={(e) => {
                setAccountId(e.target.value || null);
              }}
              inputProps={{ 'aria-label': t('email.send.account') }}
            >
              {accounts.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.fromEmail}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => previewMutation.mutate()}
              disabled={isPending || !selectedTemplateId || noAccounts}
            >
              {t('email.send.preview')}
            </Button>
          </Stack>

          {previewError}
          {preview ? (
            <EmailBodyPreview
              subject={subject}
              body={body}
              onSubjectChange={setSubject}
              onBodyChange={setBody}
            />
          ) : null}

          {noTemplates ? <Alert severity="warning">{t('email.send.noTemplates')}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => sendMutation.mutate()}
          disabled={isPending || sendMutation.isSuccess || !selectedTemplateId || noAccounts || noTemplates || toInvalid}
        >
          {t('email.send.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
